import type { ApplicationTableProps } from '../types/components'
import { Fragment, useMemo, type MouseEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { Check, AlertCircle, User, Smartphone, ChevronsUpDown } from 'lucide-react'
import type { Application } from '../types/application'
import {
  formatApplicantId,
  formatApplicationId,
  formatPhoneNumber,
  formatPaymentDueDate,
  formatDateTime,
  formatEmail,
  isCertificatePaid,
  hasAssignedModel,
} from '../utils/applicationFormatters'
import {
  APPLICATION_STATUS_OPTIONS,
  LIBERTY_ROLES_FOR_APPLICATION_LIST,
} from '../utils/applicationConstants'
import { hasPolicy, canUserOpenApplicationDetail } from '../utils/applicationPolicies'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { Tooltip } from '@/components/ui/Tooltip'
import { SectionInfoIcon } from '@/components/ui/SectionInfoIcon'
import {
  APPLICATION_LIST_COLUMN_OPTIONS,
  APPLICATION_LIST_DATA_COLUMN_IDS,
  APPLICATION_LIST_LOCKED_COLUMN_IDS,
  isApplicationListLockedColumn,
  type ApplicationListColumnId,
} from '../utils/applicationListColumns'
import styles from '../styles/applicationList.module.css'


export function ApplicationTable({
  applications,
  onSort,
  sortBy,
  orderBy,
  isProcessing = false,
  onView,
  onImpersonate,
  onShowDenialReasons,
  onOpenProductDetails,
  visibleColumnIds,
  orderedColumnIds,
}: ApplicationTableProps) {
  const user = useAppSelector(selectUser)
  const visibleColumnSet = useMemo(
    () =>
      new Set<ApplicationListColumnId>(
        visibleColumnIds ?? APPLICATION_LIST_COLUMN_OPTIONS.map((column) => column.id),
      ),
    [visibleColumnIds],
  )
  const isColumnVisible = (column: ApplicationListColumnId) => visibleColumnSet.has(column)

  const SortIcon = ({ column }: { column: string }) => {
    const isActive = orderBy === column
    return (
      <span
        className={`${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`}
        data-testid={`application-list-table-sort-${column}`}
        aria-hidden
      >
        {isActive ? (sortBy === 'asc' ? '▲' : '▼') : <ChevronsUpDown className="h-3 w-3" />}
      </span>
    )
  }

  // Policy checks
  const canOpenApplicationDetail = canUserOpenApplicationDetail(user)
  const canViewProgress = user ? hasPolicy(user, ['application_detail_view']) : true
  const canViewCreditScore =
    user &&
    hasPolicy(user, ['application_detail_view']) &&
    hasPolicy(user, ['application_list_credit_score_view'])
  const canShowDenialIcon = user ? hasPolicy(user, ['show_denied_reason_icon']) : false
  const canImpersonate = user ? hasPolicy(user, ['user_management_impersonate_applicant']) : false
  const canShowDAFIcon = user ? hasPolicy(user, ['allow_dynamic_access_fee_signal_icon']) : false
  const canShowProductColumn = user ? hasPolicy(user, ['application_list_product_filter']) : false
  const canShowModelColumn = user ? hasPolicy(user, ['application_list_model_filter']) : false
  const canShowApplicationScreeningApproachColumn = user
    ? hasPolicy(user, ['application_list_application_screening_approach_filter'])
    : false
  const canShowApplicantScreeningApproachColumn = user
    ? hasPolicy(user, ['application_list_applicant_screening_approach_filter'])
    : false
  const canShowApplicationRiskRatingColumn = user
    ? hasPolicy(user, ['application_list_application_risk_rating_filter'])
    : false
  const canShowApplicantRiskRatingColumn = user
    ? hasPolicy(user, ['application_list_applicant_risk_rating_filter'])
    : false

  // Matches Angular `liberty_roleslist` for Underwriter column visibility
  const isLibertyRole = user?.current_role?.role_name
    ? LIBERTY_ROLES_FOR_APPLICATION_LIST.includes(user.current_role.role_name)
    : false

  const isDataColumnAvailable = (column: ApplicationListColumnId) => {
    if (isApplicationListLockedColumn(column)) return true
    if (column === 'product_name') return canShowProductColumn && isColumnVisible(column)
    if (column === 'model_name') return canShowModelColumn && isColumnVisible(column)
    if (column === 'application_screening_approach') {
      return canShowApplicationScreeningApproachColumn && isColumnVisible(column)
    }
    if (column === 'applicant_screening_approach') {
      return canShowApplicantScreeningApproachColumn && isColumnVisible(column)
    }
    if (column === 'application_risk_rating') {
      return canShowApplicationRiskRatingColumn && isColumnVisible(column)
    }
    if (column === 'applicant_risk_rating') {
      return canShowApplicantRiskRatingColumn && isColumnVisible(column)
    }
    if (column === 'userwriter_name') return isLibertyRole && isColumnVisible(column)
    if (column === 'progress_status') return canViewProgress && isColumnVisible(column)
    if (column === 'corelogic_applicant_score') return Boolean(canViewCreditScore) && isColumnVisible(column)
    if (column === 'income_score') return Boolean(canViewCreditScore) && isColumnVisible(column)
    if (column === 'impersonate') return false
    return APPLICATION_LIST_DATA_COLUMN_IDS.includes(column) && isColumnVisible(column)
  }

  const orderedVisibleDataColumnIds = (
    orderedColumnIds ?? APPLICATION_LIST_COLUMN_OPTIONS.map((column) => column.id)
  )
    .filter((column): column is Exclude<ApplicationListColumnId, 'impersonate'> =>
      APPLICATION_LIST_DATA_COLUMN_IDS.includes(column as Exclude<ApplicationListColumnId, 'impersonate'>) &&
      isDataColumnAvailable(column),
    )
    .filter(
      (column, index, columns) =>
        !isApplicationListLockedColumn(column) ||
        columns.indexOf(column) === index,
    )

  const lockedDataColumnIds = APPLICATION_LIST_LOCKED_COLUMN_IDS.filter((column) =>
    isDataColumnAvailable(column),
  )
  const customizableDataColumnIds = orderedVisibleDataColumnIds.filter(
    (column) => !isApplicationListLockedColumn(column),
  )
  const tableDataColumnIds = [...lockedDataColumnIds, ...customizableDataColumnIds]
  const hasVisibleDataColumns = tableDataColumnIds.length > 0

  const isCompanyOrPropertyRole = user?.current_role?.role_name
    ? user.current_role.role_name.startsWith('ROLE_COMPANY') || user.current_role.role_name.startsWith('ROLE_PROPERTY')
    : false

  const getCompactMinW = (col: string, defaultClass: string) => {
    const map: Record<string, string> = {
      applicant_id: 'min-w-[100px]',
      application_id: 'min-w-[100px]',
      property_name: 'min-w-[120px]',
      product_name: 'min-w-[120px]',
      model_name: 'min-w-[120px]',
      applicant_screening_approach: 'min-w-[140px]',
      application_screening_approach: 'min-w-[140px]',
      application_risk_rating: 'min-w-[140px]',
      applicant_risk_rating: 'min-w-[140px]',
      resident_name: 'min-w-[130px]',
      email: 'min-w-[160px]',
      userwriter_name: 'min-w-[120px]',
      invite_code: 'min-w-[100px]',
      mobile_no: 'min-w-[110px]',
      co_app: 'min-w-[80px]',
      payment_due: 'min-w-[100px]',
      underwriting_sort: 'min-w-[120px]',
      applicant_status: 'min-w-[130px]',
      progress_status: 'min-w-[130px]',
      past_customer: 'min-w-[100px]',
      corelogic_applicant_score: 'min-w-[90px]',
      income_score: 'min-w-[90px]',
      certificate_paid: 'min-w-[100px]'
    }
    return map[col] || defaultClass
  }

  const renderSortableHeader = ({
    column,
    children,
    className = '',
    align = 'left',
  }: {
    column: string
    children: ReactNode
    className?: string
    align?: 'left' | 'center'
  }) => {
    const triggerSort = () => {
      if (isProcessing) return
      onSort(column)
    }

    return (
      <th
        data-testid={`application-list-table-header-${column}`}
        className={`${isCompanyOrPropertyRole ? getCompactMinW(column, className) : className} ${align === 'center' ? 'text-center' : ''} cursor-pointer`}
        onClick={triggerSort}
      >
        <button
          type="button"
          data-testid={`application-list-table-header-${column}-sort-button`}
          className={`${styles.sortHeaderButton} ${align === 'center' ? 'w-full justify-center' : 'justify-start'}`}
          disabled={isProcessing}
          onClick={(event) => {
            event.stopPropagation()
            triggerSort()
          }}
        >
          <span className={styles.sortHeaderLabel}>{children}</span>
          <SortIcon column={column} />
        </button>
      </th>
    )
  }

  const renderStaticHeader = ({
    column,
    children,
    className = '',
    align = 'left',
  }: {
    column: string
    children: ReactNode
    className?: string
    align?: 'left' | 'center'
  }) => (
    <th
      data-testid={`application-list-table-header-${column}`}
      className={`${isCompanyOrPropertyRole ? getCompactMinW(column, className) : className} ${align === 'center' ? 'text-center' : ''}`}
    >
      {children}
    </th>
  )

  const getStatusLabel = (status: string): string => {
    const statusOption = APPLICATION_STATUS_OPTIONS.find((opt) => opt.value === status)
    return statusOption?.label || status
  }

  const handleApplicantClick = (
    event: MouseEvent<HTMLAnchorElement>,
    applicantId: number,
  ) => {
    if (!canOpenApplicationDetail) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return
    }

    if (onView) {
      event.preventDefault()
      onView(applicantId)
    }
  }

  const renderColumnHeader = (
    column: Exclude<ApplicationListColumnId, 'impersonate'>,
  ) => {
    switch (column) {
      case 'applicant_id':
        return renderSortableHeader({ column, className: 'min-w-[140px]', children: 'Applicant' })
      case 'application_id':
        return renderSortableHeader({ column, className: 'min-w-[140px]', children: 'Application' })
      case 'property_name':
        return renderSortableHeader({ column, className: 'min-w-[160px]', children: 'Property' })
      case 'product_name':
        return renderSortableHeader({ column, className: 'min-w-[160px]', children: 'Product' })
      case 'model_name':
        return renderSortableHeader({ column, className: 'min-w-[160px]', children: 'Model' })
      case 'application_risk_rating':
        return renderSortableHeader({ column, className: 'min-w-[180px]', children: 'Application Risk Rating' })
      case 'applicant_risk_rating':
        return renderSortableHeader({ column, className: 'min-w-[180px]', children: 'Applicant Risk Rating' })
      case 'application_screening_approach':
        return renderSortableHeader({ column, className: 'min-w-[180px]', children: 'Application Screening Approach' })
      case 'applicant_screening_approach':
        return renderSortableHeader({ column, className: 'min-w-[180px]', children: 'Applicant Screening Approach' })
      case 'resident_name':
        return renderSortableHeader({ column, className: 'min-w-[180px]', children: 'Resident Name' })
      case 'email':
        return renderSortableHeader({ column, className: 'min-w-[220px]', children: 'Email' })
      case 'userwriter_name':
        return renderSortableHeader({ column, className: 'min-w-[160px]', children: 'Underwriter' })
      case 'invite_code':
        return renderStaticHeader({ column, className: 'min-w-[120px]', children: 'Invite Code' })
      case 'mobile_no':
        return renderSortableHeader({ column, className: 'min-w-[140px]', children: 'Phone Number' })
      case 'co_app':
        return renderStaticHeader({ column, className: 'min-w-[100px]', children: 'Co-App' })
      case 'payment_due':
        return renderStaticHeader({ column, className: 'min-w-[120px]', children: 'Payment Due' })
      case 'underwriting_sort':
        return renderSortableHeader({ column, className: 'min-w-[160px]', children: 'Last Updated' })
      case 'applicant_status':
        return renderSortableHeader({ column, className: 'min-w-[180px]', children: 'Status' })
      case 'progress_status':
        return renderSortableHeader({ column, className: 'min-w-[180px]', children: 'Progress' })
      case 'past_customer':
        return renderSortableHeader({ column, className: 'min-w-[140px]', children: 'Past Customer' })
      case 'corelogic_applicant_score':
        return renderSortableHeader({
          column,
          className: 'min-w-[120px]',
          align: 'center',
          children: 'Credit Score',
        })
      case 'income_score':
        return renderSortableHeader({
          column,
          className: 'min-w-[120px]',
          align: 'center',
          children: 'Income Score',
        })
      case 'certificate_paid':
        return renderSortableHeader({
          column,
          className: 'min-w-[120px]',
          align: 'center',
          children: 'Contract Paid',
        })
      default:
        return null
    }
  }

  const renderColumnCell = (
    column: Exclude<ApplicationListColumnId, 'impersonate'>,
    application: Application,
    index: number,
    rowTestId: string,
  ) => {
    const isDenied = application.applicant_status?.toLowerCase() === 'denied'
    const showDAFIcon = Boolean(application.allow_dynamic_access_fee) && canShowDAFIcon

    switch (column) {
      case 'applicant_id':
        return (
          <td data-testid={`${rowTestId}-applicant-cell`}>
            {!canOpenApplicationDetail || isCompanyOrPropertyRole ? (
              <span data-testid={`${rowTestId}-applicant-text`}>
                {formatApplicantId(application.applicant_id)}
              </span>
            ) : (
              <Link
                to={`/application/${application.applicant_id}`}
                onClick={(event) => handleApplicantClick(event, application.applicant_id)}
                className={`${styles.link} border-0 bg-transparent p-0 text-left`}
                style={{ cursor: 'pointer' }}
                data-testid={`${rowTestId}-applicant-link`}
              >
                {formatApplicantId(application.applicant_id)}
              </Link>
            )}
          </td>
        )
      case 'application_id':
        return (
          <td data-testid={`${rowTestId}-application-cell`}>
            {formatApplicationId(application.application_id)}
          </td>
        )
      case 'property_name':
        return <td data-testid={`${rowTestId}-property-cell`}>{application.property_name}</td>
      case 'product_name':
        return (
          <td data-testid={`${rowTestId}-product-cell`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span data-testid={`${rowTestId}-product-name`}>
                {application.product_name || '—'}
              </span>
              {showDAFIcon && (
                <SectionInfoIcon
                  align="start"
                  maxWidth={360}
                  iconSize={16}
                  isLastRow={index >= Math.max(0, applications.length - 3)}
                  aria-label="Product details"
                  data-testid={`application-list-table-product-popover-${application.applicant_id}`}
                  onClick={() => onOpenProductDetails?.(application)}
                  clampPanelHeight
                  contentClassName="px-3 py-2 text-xs text-slate-700"
                  content={
                    <>
                      <p className="mb-1.5 font-semibold text-slate-800">
                        {application.product_name || 'Product'}
                      </p>
                      {application.product_description?.trim() ? (
                        <p className="whitespace-pre-wrap">
                          {application.product_description}
                        </p>
                      ) : (
                        <p className="whitespace-pre-wrap">No description.</p>
                      )}
                    </>
                  }
                />
              )}
            </div>
          </td>
        )
      case 'model_name':
        return (
          <td data-testid={`${rowTestId}-model-cell`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span data-testid={`${rowTestId}-model-name`}>
                {application.model_name || '—'}
              </span>
              {hasAssignedModel(application) && (
                <SectionInfoIcon
                  align="start"
                  maxWidth={360}
                  iconSize={16}
                  isLastRow={index >= Math.max(0, applications.length - 3)}
                  aria-label="Model details"
                  data-testid={`application-list-table-model-popover-${application.applicant_id}`}
                  clampPanelHeight
                  contentClassName="px-3 py-2 text-xs text-slate-700"
                  content={
                    <>
                      <p className="mb-1.5 font-semibold text-slate-800">
                        {application.model_name || 'Model'}
                      </p>
                      {application.model_description?.trim() ? (
                        <p className="whitespace-pre-wrap">
                          {application.model_description}
                        </p>
                      ) : (
                        <p className="whitespace-pre-wrap">No description.</p>
                      )}
                    </>
                  }
                />
              )}
            </div>
          </td>
        )
      case 'application_risk_rating':
        return (
          <td data-testid={`${rowTestId}-application-risk-rating-cell`}>
            {application.application_risk_rating || '—'}
          </td>
        )
      case 'applicant_risk_rating':
        return (
          <td data-testid={`${rowTestId}-applicant-risk-rating-cell`}>
            {application.applicant_risk_rating || '—'}
          </td>
        )
      case 'application_screening_approach':
        return (
          <td data-testid={`${rowTestId}-application-screening-approach-cell`}>
            {application.application_screening_approach || '—'}
          </td>
        )
      case 'applicant_screening_approach':
        return (
          <td data-testid={`${rowTestId}-applicant-screening-approach-cell`}>
            {application.applicant_screening_approach || '—'}
          </td>
        )
      case 'resident_name':
        return <td data-testid={`${rowTestId}-resident-name-cell`}>{application.resident_name}</td>
      case 'email':
        return <td data-testid={`${rowTestId}-email-cell`}>{formatEmail(application.email)}</td>
      case 'userwriter_name':
        return (
          <td className="text-center" data-testid={`${rowTestId}-underwriter-cell`}>
            {application.userwriter_name || ''}
          </td>
        )
      case 'invite_code':
        return <td data-testid={`${rowTestId}-invite-code-cell`}>{application.invite_code}</td>
      case 'mobile_no':
        return (
          <td data-testid={`${rowTestId}-phone-cell`}>
            {formatPhoneNumber(application.mobile_no)}
          </td>
        )
      case 'co_app':
        return (
          <td data-testid={`${rowTestId}-co-app-cell`}>
            {application.other_applicant === 1 ? 'Yes' : 'No'}
          </td>
        )
      case 'payment_due':
        return (
          <td data-testid={`${rowTestId}-payment-due-cell`}>
            {application.payment_due_date ? formatPaymentDueDate(application.payment_due_date) : ''}
          </td>
        )
      case 'underwriting_sort':
        return (
          <td data-testid={`${rowTestId}-last-updated-cell`}>
            {application.progress_status_date ? formatDateTime(application.progress_status_date) : ''}
          </td>
        )
      case 'applicant_status':
        return (
          <td data-testid={`${rowTestId}-status-cell`}>
            <div className={styles.popupMenu} style={{ minWidth: '150px' }}>
              <span data-testid={`${rowTestId}-status-label`}>
                {getStatusLabel(application.applicant_status)}
              </span>
              {isDenied && canShowDenialIcon && (
                <Tooltip content="Click for detail">
                  <button
                    type="button"
                    className="m-0 inline-flex cursor-pointer select-none border-0 bg-transparent p-0 focus:outline-none focus:ring-2 focus:ring-blue-200 rounded"
                    aria-label="View denial reasons"
                    data-testid={`${rowTestId}-denial-reasons-button`}
                    onClick={() => onShowDenialReasons?.(application.applicant_id)}
                  >
                    <AlertCircle className={styles.denialIcon} aria-hidden />
                  </button>
                </Tooltip>
              )}
            </div>
          </td>
        )
      case 'progress_status':
        return <td data-testid={`${rowTestId}-progress-cell`}>{application.progress_status}</td>
      case 'past_customer':
        return (
          <td data-testid={`${rowTestId}-past-customer-cell`}>
            {application.past_customer === '1' ? 'Yes' : 'No'}
          </td>
        )
      case 'corelogic_applicant_score':
        return (
          <td className="text-center" data-testid={`${rowTestId}-credit-score-cell`}>
            {application.corelogic_applicant_score
              ? String(application.corelogic_applicant_score)
              : ''}
          </td>
        )
      case 'income_score':
        return (
          <td className="text-center" data-testid={`${rowTestId}-income-score-cell`}>
            {application.income_score !== null &&
            application.income_score !== undefined &&
            application.income_score !== ''
              ? String(application.income_score)
              : ''}
          </td>
        )
      case 'certificate_paid':
        return (
          <td className="text-center" data-testid={`${rowTestId}-contract-paid-cell`}>
            {isCertificatePaid(application.certificate_paid) && (
              <Check
                className={styles.checkIcon}
                data-testid={`${rowTestId}-contract-paid-icon`}
                aria-hidden
              />
            )}
          </td>
        )
      default:
        return null
    }
  }

  return (
    <div className={styles.tableCard} data-testid="application-list-table-wrap">
      <div
        className="flex bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        data-testid="application-list-table-layout"
      >
        {/* LEFT SIDE: Scrollable Data Columns */}
        <div
          className="flex-1 overflow-x-auto touch-scroll min-w-0"
          data-testid="application-list-table-scroll"
        >
          {hasVisibleDataColumns ? (
          <table className={`${styles.listingTable} ${isCompanyOrPropertyRole ? styles.compactTable : ''}`} data-testid="application-list-table">
            <thead data-testid="application-list-table-head">
              <tr
                className={`h-[54px] ${isProcessing ? 'pointer-events-none' : ''}`}
                data-testid="application-list-table-header-row"
              >
                {tableDataColumnIds.map((column) => (
                  <Fragment key={column}>{renderColumnHeader(column)}</Fragment>
                ))}
              </tr>
            </thead>
            <tbody data-testid="application-list-table-body">
              {applications.map((application, index) => {
                const rowTestId = `application-list-row-${application.applicant_id}`

                return (
                  <tr
                    key={application.applicant_id}
                    className="h-[76px]"
                    data-testid={rowTestId}
                  >
                    {tableDataColumnIds.map((column) => (
                      <Fragment key={column}>
                        {renderColumnCell(column, application, index, rowTestId)}
                      </Fragment>
                    ))}
                  </tr>
                )
              })}
              </tbody>
            </table>
          ) : (
            <div
              className="flex min-h-[160px] items-center justify-center px-6 py-10 text-center text-sm font-medium text-slate-500"
              data-testid="application-list-table-no-columns"
            >
              No columns selected. Use Columns to choose fields for this table.
            </div>
          )}
        </div>

        {/* RIGHT SIDE: Fixed Action Column */}
        {canImpersonate && (
          <div
            className="shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)] z-10 bg-white"
            data-testid="application-list-table-actions-column"
          >
            <table
              className={styles.listingTable}
              data-testid="application-list-table-actions"
            >
              <thead data-testid="application-list-table-actions-head">
                <tr className="h-[54px]">
                  <th
                    className="min-w-[120px] text-center px-4 bg-slate-50 border-b-2 border-slate-200"
                    data-testid="application-list-table-header-actions"
                  >
                    Impersonate
                  </th>
                </tr>
              </thead>
              <tbody data-testid="application-list-table-actions-body">
                {applications.map((application) => {
                  const rowTestId = `application-list-row-${application.applicant_id}`
                  return (
                    <tr
                      key={`action-${application.applicant_id}`}
                      className="h-[76px]"
                      data-testid={`${rowTestId}-actions-row`}
                    >
                      <td
                        className="px-4 text-center"
                        data-testid={`${rowTestId}-actions-cell`}
                      >
                        {application.role_id && (
                          <div
                            className={styles.impersonateActions}
                            style={{ justifyContent: 'center' }}
                            data-testid={`${rowTestId}-actions`}
                          >
                            <Tooltip content="Impersonate">
                              <button
                                type="button"
                                className={styles.iconButton}
                                onClick={() =>
                                  onImpersonate?.(application.role_id!, application.applicant_id)
                                }
                                aria-label="Impersonate"
                                data-testid={`${rowTestId}-impersonate-button`}
                              >
                                <User className="h-4 w-4" aria-hidden />
                              </button>
                            </Tooltip>
                            <Tooltip content="Impersonate Mobile">
                              <button
                                type="button"
                                className={styles.iconButton}
                                onClick={() =>
                                  onImpersonate?.(
                                    application.role_id!,
                                    application.applicant_id,
                                    true,
                                  )
                                }
                                aria-label="Impersonate Mobile"
                                data-testid={`${rowTestId}-impersonate-mobile-button`}
                              >
                                <Smartphone className="h-4 w-4" aria-hidden />
                              </button>
                            </Tooltip>
                          </div>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
